// src/patients/services/use-care-model-tasks.ts
// The patient's active care-model runs, each with the Checklist step it waits on (see `@/patients/lib/care-model-tasks`),
// and the tick that resumes the run.
//
// Four outcomes stay apart, as for the care-plan read: still reading; the enrolment list failed (a failed read, never
// "no runs"); no active run; the runs. One run whose detail cannot be read still shows, with its current step name
// from C4 and a line that the detail could not be read: it is never dropped and never shown as "nothing to do".
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { fetchPatientEnrolments } from '@/encounter/services/care-model-api';
import {
  completeChecklistItem,
  fetchDefinitionVersion,
  fetchEnrolmentRun,
  fetchExecutionLog,
  fetchWorkflowInstance,
} from '@/encounter/services/care-model-run-api';
import { carePlansKey, patientEnrolmentsKey } from '@/encounter/services/use-care-plans';
import {
  parseExecutionLog,
  parseRunDefinition,
  parseRunInstance,
  parseRunPointer,
  readTicks,
  rememberTick,
  TickUnconfirmedError,
  tickConfirmed,
  waitingChecklist,
  type TickStorage,
  type WaitingChecklist,
} from '@/patients/lib/care-model-tasks';
import type { PatientEnrolmentWire } from '@/types/care-model-api';

export interface CareModelRun {
  enrolmentId: string;
  /** The care-model definition the enrolment runs (C4), the key a care plan joins on (`care-plan-view.ts`). */
  definitionId?: string | null;
  /** The care model's name; null when its definition could not be read. */
  modelName: string | null;
  /** The step the run waits on, as C4 names it; null when it waits on none. */
  currentStepName: string | null;
  /** The checklist it waits on; null when it waits on another kind of step or on nothing. */
  checklist: WaitingChecklist | null;
  /** The run's detail could not be read: the checklist is unknown, not absent. */
  unreadable: boolean;
}

/** The practice-wide list of waiting care-model reviews (Tasks page, task management R1). Kept here so a tick refreshes it. */
export const careModelQueueKey = ['care-model-queue'] as const;

export function careModelTasksKey(patientId: string | null | undefined) {
  return ['care-model-tasks', patientId] as const;
}

/** Reads a definition version by id. A caller reading many runs passes one shared, de-duplicating reader. */
export type DefinitionReader = (definitionVersionId: string, signal?: AbortSignal) => Promise<unknown>;

export async function readRun(
  e: PatientEnrolmentWire & { enrolmentId: string },
  signal?: AbortSignal,
  readDefinition: DefinitionReader = fetchDefinitionVersion,
): Promise<CareModelRun> {
  const base = { enrolmentId: e.enrolmentId, definitionId: e.definitionId ?? null, currentStepName: e.currentStep?.name?.trim() || null };
  try {
    const instanceId = parseRunPointer(await fetchEnrolmentRun(e.enrolmentId, signal));
    if (!instanceId) throw new Error('run: no instance id');
    const [instanceBody, logBody] = await Promise.all([fetchWorkflowInstance(instanceId, signal), fetchExecutionLog(instanceId, signal)]);
    const instance = parseRunInstance(instanceBody);
    const log = parseExecutionLog(logBody);
    if (!instance || !log) throw new Error('run: unreadable instance or log');
    const definition = parseRunDefinition(await readDefinition(instance.definitionVersionId, signal));
    if (!definition) throw new Error('run: unreadable definition');
    return { ...base, modelName: definition.name, checklist: waitingChecklist(instance, log, definition, e.currentStep?.id), unreadable: false };
  } catch (error) {
    if (signal?.aborted) throw error;
    return { ...base, modelName: null, checklist: null, unreadable: true };
  }
}

export async function fetchCareModelRuns(patientId: string, signal?: AbortSignal): Promise<CareModelRun[]> {
  const enrolments = await fetchPatientEnrolments(patientId, signal);
  const active = enrolments.filter(
    (e): e is PatientEnrolmentWire & { enrolmentId: string } => !!e.enrolmentId && (e.status ?? '').toLowerCase() === 'active',
  );
  return Promise.all(active.map((e) => readRun(e, signal)));
}

/** `enabled` false (not a physician) → no request at all. */
export function useCareModelRuns(patientId: string | null | undefined, enabled: boolean): UseQueryResult<CareModelRun[]> {
  return useQuery<CareModelRun[]>({
    queryKey: careModelTasksKey(patientId),
    enabled: enabled && !!patientId,
    queryFn: ({ signal }) => fetchCareModelRuns(patientId as string, signal),
  });
}

function tickStorage(): TickStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export interface ChecklistTick {
  /** Labels ticked from this browser on THIS run and step (the engine has no per-item read). */
  ticked: ReadonlySet<string>;
  tick: (label: string) => void;
  pendingLabel: string | null;
  error: unknown;
}

/**
 * One tick at a time per step; never retried on a guess. A success re-reads the runs and the care plan. The caller keys
 * the component by instance and step, so this state never carries over to another checklist.
 */
export function useChecklistTick(patientId: string, step: Pick<WaitingChecklist, 'instanceId' | 'activityId'>): ChecklistTick {
  const qc = useQueryClient();
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => readTicks(tickStorage(), step.instanceId, step.activityId));
  const mutation = useMutation<unknown, Error, string>({
    mutationFn: async (label) => {
      const body = await completeChecklistItem(step.instanceId, step.activityId, label);
      // Only a confirmed completion is remembered as done; anything else is said and the run is read again.
      if (!tickConfirmed(body)) throw new TickUnconfirmedError();
      return body;
    },
    retry: false,
    // The block says its own failure in words; the global toast would say it twice.
    meta: { skipGlobalError: true },
    onError: (error) => {
      if (error instanceof TickUnconfirmedError) {
        void qc.invalidateQueries({ queryKey: careModelTasksKey(patientId) });
        void qc.invalidateQueries({ queryKey: careModelQueueKey });
      }
    },
    onSuccess: (_data, label) => {
      const stored = rememberTick(tickStorage(), step.instanceId, step.activityId, label);
      setTicked((prev) => new Set([...prev, ...stored]));
      return Promise.all([
        qc.invalidateQueries({ queryKey: careModelTasksKey(patientId) }),
        qc.invalidateQueries({ queryKey: patientEnrolmentsKey(patientId) }),
        qc.invalidateQueries({ queryKey: carePlansKey(patientId) }),
        qc.invalidateQueries({ queryKey: careModelQueueKey }),
      ]);
    },
  });
  return {
    ticked,
    tick: (label) => {
      if (!mutation.isPending) mutation.mutate(label);
    },
    pendingLabel: mutation.isPending ? (mutation.variables ?? null) : null,
    error: mutation.error,
  };
}
